<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKnowledgeBase } from 'dashboard/composables/useKnowledgeBase';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';
import EntryForm from './components/EntryForm.vue';
import EntryCard from './components/EntryCard.vue';

const { t } = useI18n();

const { canEditSection } = useSectionPermissions();

const {
  entries,
  bots,
  loading,
  meta,
  totalPages,
  fetchEntries,
  fetchBots,
  createEntry,
  updateEntry,
  deleteEntry,
  changePage,
  regenerateEmbedding,
  regenerateAllEmbeddings,
} = useKnowledgeBase();

// UI State
const showForm = ref(false);
const editingEntry = ref(null);
const searchQuery = ref('');
const selectedBotId = ref(null);

// Selected bot object
const selectedBot = computed(() => {
  if (!selectedBotId.value) return null;
  return bots.value.find(b => b.id === selectedBotId.value);
});

// Filtered entries by search query
const filteredEntries = computed(() => {
  if (!searchQuery.value) {
    return entries.value;
  }
  const query = searchQuery.value.toLowerCase();
  return entries.value.filter(
    e =>
      e.question?.toLowerCase().includes(query) ||
      e.answer?.toLowerCase().includes(query)
  );
});

// Check if there are bots available
const hasBots = computed(() => bots.value.length > 0);

// Check if there are entries
const hasEntries = computed(() => entries.value.length > 0);

// Count entries without embedding
const entriesWithoutEmbedding = computed(() => {
  return entries.value.filter(e => !e.embedding).length;
});

// Count entries by bot
const entriesByBot = computed(() => {
  const countMap = {};
  bots.value.forEach(bot => {
    countMap[bot.id] = 0;
  });
  return countMap;
});

onMounted(async () => {
  await fetchBots();
  // Select first bot if available
  if (bots.value.length > 0) {
    selectedBotId.value = bots.value[0].id;
    await fetchEntries({ agent_bot_id: selectedBotId.value });
  }
});

const handleSelectBot = async botId => {
  selectedBotId.value = botId;
  searchQuery.value = '';
  await fetchEntries({ agent_bot_id: botId });
};

const handleNewEntry = () => {
  if (!selectedBotId.value) return;
  editingEntry.value = { agent_bot_id: selectedBotId.value };
  showForm.value = true;
};

const handleEditEntry = entry => {
  editingEntry.value = entry;
  showForm.value = true;
};

const handleSaveEntry = async entryData => {
  if (editingEntry.value?.id) {
    await updateEntry(editingEntry.value.id, entryData);
  } else {
    await createEntry(entryData);
  }
  showForm.value = false;
  editingEntry.value = null;
  // Refresh entries for the selected bot
  if (selectedBotId.value) {
    await fetchEntries({ agent_bot_id: selectedBotId.value });
  }
};

const handleDeleteEntry = async id => {
  if (confirm(t('OMNI.KNOWLEDGE_BASE.CONFIRM_DELETE'))) {
    await deleteEntry(id);
  }
};

const handlePageChange = async page => {
  const additionalParams = selectedBotId.value
    ? { agent_bot_id: selectedBotId.value }
    : {};
  await changePage(page, additionalParams);
};

const handleRegenerateEmbedding = async id => {
  await regenerateEmbedding(id);
};

const handleRegenerateAllMissing = async () => {
  if (confirm(t('OMNI.KNOWLEDGE_BASE.CONFIRM_REGENERATE_ALL'))) {
    await regenerateAllEmbeddings(true);
  }
};
</script>

<template>
  <div class="flex flex-col h-full w-full min-w-0">
    <!-- Header -->
    <div
      class="flex items-center justify-between p-4 border-b border-slate-75 dark:border-slate-800"
    >
      <h1 class="text-xl font-semibold text-slate-900 dark:text-slate-25">
        {{ t('OMNI.KNOWLEDGE_BASE.HEADER') }}
      </h1>
      <div class="flex gap-2">
        <button
          v-if="selectedBotId && entriesWithoutEmbedding > 0"
          class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-orange-600 bg-orange-50 dark:bg-orange-900/20 rounded-md hover:bg-orange-100 dark:hover:bg-orange-900/30"
          @click="handleRegenerateAllMissing"
        >
          <span class="i-lucide-refresh-cw" />
          {{ t('OMNI.KNOWLEDGE_BASE.REGENERATE_MISSING') }} ({{
            entriesWithoutEmbedding
          }})
        </button>
        <button
          v-if="selectedBotId && canEditSection('response_templates')"
          class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!selectedBotId"
          @click="handleNewEntry"
        >
          <span class="i-lucide-plus" />
          {{ t('OMNI.KNOWLEDGE_BASE.NEW_ENTRY') }}
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-hidden p-4">
      <!-- No bots message -->
      <div
        v-if="!hasBots"
        class="flex flex-col items-center justify-center h-full text-center p-8"
      >
        <span
          class="i-lucide-bot text-6xl text-slate-300 dark:text-slate-600 mb-4"
        />
        <h2
          class="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2"
        >
          {{ t('OMNI.KNOWLEDGE_BASE.NO_BOTS_TITLE') }}
        </h2>
        <p class="text-sm text-slate-500 dark:text-slate-400 max-w-md">
          {{ t('OMNI.KNOWLEDGE_BASE.NO_BOTS_MESSAGE') }}
        </p>
      </div>

      <!-- Content with bots -->
      <div v-else class="flex gap-4 h-full">
        <!-- Bots Sidebar -->
        <div
          class="w-56 bg-white dark:bg-slate-900 rounded-lg border border-slate-75 dark:border-slate-800 p-4 flex flex-col overflow-hidden flex-shrink-0"
        >
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-25">
              {{ t('OMNI.KNOWLEDGE_BASE.BOTS') }}
            </h2>
          </div>

          <!-- Bot List -->
          <div class="flex-1 overflow-y-auto space-y-2">
            <button
              v-for="bot in bots"
              :key="bot.id"
              class="w-full text-left px-3 py-2 rounded-lg transition-colors"
              :class="
                selectedBotId === bot.id
                  ? 'bg-woot-50 dark:bg-woot-900/20 text-woot-600'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-800'
              "
              @click="handleSelectBot(bot.id)"
            >
              <div class="flex items-center gap-2">
                <span class="i-lucide-bot w-4 h-4" />
                <span class="text-sm font-medium truncate">{{ bot.name }}</span>
              </div>
            </button>
          </div>
        </div>

        <!-- Entries List -->
        <div
          class="flex-1 bg-white dark:bg-slate-900 rounded-lg border border-slate-75 dark:border-slate-800 p-4 flex flex-col overflow-hidden min-w-0"
        >
          <!-- Header with selected bot name -->
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-25">
              <template v-if="selectedBot">
                {{ selectedBot.name }} - {{ t('OMNI.KNOWLEDGE_BASE.TITLE') }}
              </template>
              <template v-else>
                {{ t('OMNI.KNOWLEDGE_BASE.SELECT_BOT') }}
              </template>
            </h2>
          </div>

          <!-- Search bar when bot selected -->
          <div v-if="selectedBotId && hasEntries" class="mb-4">
            <input
              v-model="searchQuery"
              type="text"
              class="w-full px-2 py-1.5 text-xs border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-shadow"
              :placeholder="t('OMNI.KNOWLEDGE_BASE.SEARCH_PLACEHOLDER')"
            />
          </div>

          <!-- No bot selected -->
          <div
            v-if="!selectedBotId"
            class="flex-1 flex flex-col items-center justify-center text-center p-4"
          >
            <span
              class="i-lucide-arrow-left text-4xl text-slate-300 dark:text-slate-600 mb-2"
            />
            <p class="text-sm text-slate-600 dark:text-slate-400">
              {{ t('OMNI.KNOWLEDGE_BASE.SELECT_BOT_MESSAGE') }}
            </p>
          </div>

          <!-- Loading -->
          <div
            v-else-if="loading"
            class="flex-1 flex items-center justify-center"
          >
            <span
              class="i-lucide-loader-2 w-8 h-8 animate-spin text-woot-500"
            />
          </div>

          <!-- Empty state -->
          <div
            v-else-if="!hasEntries"
            class="flex-1 flex flex-col items-center justify-center text-center p-4"
          >
            <span
              class="i-lucide-book-open text-4xl text-slate-300 dark:text-slate-600 mb-2"
            />
            <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
              {{ t('OMNI.KNOWLEDGE_BASE.EMPTY_STATE') }}
            </p>
            <button
              v-if="canEditSection('response_templates')"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600"
              @click="handleNewEntry"
            >
              <span class="i-lucide-plus" />
              {{ t('OMNI.KNOWLEDGE_BASE.CREATE_FIRST') }}
            </button>
          </div>

          <!-- Entries grid -->
          <div v-else class="flex-1 overflow-y-auto">
            <div class="grid grid-cols-1 xl:grid-cols-2 gap-3">
              <EntryCard
                v-for="entry in filteredEntries"
                :key="entry.id"
                :entry="entry"
                @edit="handleEditEntry"
                @delete="handleDeleteEntry"
                @regenerate="handleRegenerateEmbedding"
              />
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="mt-4 flex justify-center gap-2">
            <button
              v-for="page in totalPages"
              :key="page"
              class="px-3 py-1.5 text-sm rounded transition-colors"
              :class="
                page === meta.page
                  ? 'bg-woot-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
              "
              @click="handlePageChange(page)"
            >
              {{ page }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Modal -->
    <EntryForm
      :entry="editingEntry"
      :is-open="showForm"
      :bots="bots"
      :selected-bot-id="selectedBotId"
      @save="handleSaveEntry"
      @close="showForm = false"
    />
  </div>
</template>
